import { css, jsx } from '@emotion/core';
import { Col, Row } from 'antd';
import moment from 'moment';
import { normalize, schema } from 'normalizr';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import HeroSlides from '@/src/cms/components/CMSPage/CorePage/Blocks/HeroSlides';
import DesignPage from '@/src/cms/models/DesignPage';
import EventsList from '@/src/events/components/EventsList';
import SearchFilters from '@/src/events/components/EventsSearchFilters';
import Container from '@/src/shared/components/Container';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import Hero from '@/src/shared/components/Hero';
import Layout from '@/src/shared/components/Layout';
import Link from '@/src/shared/components/Link';
import withMembershipAccessWrap, {
  mapStateToProps,
} from '@/src/shared/components/MembershipPageWrap';
import Section from '@/src/shared/components/Section';
import WebLogImage from '@/src/shared/components/WebLogImage';
import { bp, colors } from '@/styles';
import request from '@/utils/axios';
import searchContent from '@/utils/searchContent';

const DATE_FORMAT = 'MM/DD/YYYY';
const isServer = typeof window === 'undefined';

const style = {
  carouselWrapper: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  radioButtonWrapper: css`
    .ant-radio-button-wrapper-checked {
      background: ${colors.secondaryBlue} !important;
      border-color: ${colors.secondaryBlue} !important;
      box-shadow: -1px 0 0 0 ${colors.secondaryBlue} !important;
    }
    .ant-radio-button-wrapper-checked::before {
      background: ${colors.secondaryBlue} !important;
      border-color: ${colors.secondaryBlue} !important;
      box-shadow: -1px 0 0 0 ${colors.secondaryBlue} !important;
    }
    .ant-radio-button-wrapper-checked::after {
      background: ${colors.secondaryBlue} !important;
      border-color: ${colors.secondaryBlue} !important;
      box-shadow: -1px 0 0 0 ${colors.secondaryBlue} !important;
    }
  `,
  button: css`
    color: ${colors.secondaryBlue};
    border: 1px solid #dad9d7;
    border-radius: 2px !important;
    box-sizing: border-box;
    &:hover {
      color: ${colors.secondaryBlue};
    }
    font-size: 1.2rem;
    font-weight: bold;
  `,
  container: collapsed => css`
    display: flex !important;
    flex-direction: column;
    @media ${bp.md} {
      ${collapsed
      ? css`
            flex-direction: column;
          `
      : css`
            flex-direction: row;
          `};
    }
  `,
  buttonsRow: css`
    display: flex;
    flex-wrap: wrap;
    @media ${bp.lg} {
      flex-wrap: nowrap;
    }
  `,
  monthYearWrapper: css`
    margin-top: 24px;
    @media ${bp.xs} {
      margin-left: auto;
      margin-top: 0px;
      margin-right: 10px;
    }
  `,
  select: css`
    width: 100px;
    font-size: 1.6rem;
  `,
  paginationWrapper: css`
    display: flex;
    align-items: center;
  `,
  results: css`
    font-weight: 900;
    font-size: 1.2rem;
    color: ${colors.secondaryGray};
  `,
  viewWrapper: css`
    width: 100%;
  `,
  search: css`
    @media ${bp.md} {
      margin-right: 32px;
    }
    @media print {
      display: none;
    }
  `,
  contentbox: css`
    padding: 52px 32px;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 6px 12px 0 rgba(35, 32, 32, 0.1);
  `,
  nonmemberprompt: css`
    text-align: center;
    p {
      font-size: 1.6rem;
      margin-bottom: 0;
    }
  `,
  heroContainer: css`
    @media print {
      -webkit-print-color-adjust: exact !important; /* Chrome, Safari */
      color-adjust: exact !important;
    }
  `,
  noRecordsFound: css`
    font-size: 1.6rem;
    font-weight: bold;
    text-align: center;
  `,
};

const getUserCommittees = async personId => {
  try {
    const { data } = await request({
      url: 'profile/profilecommittees',
      params: { personId },
      triggerLoad: false,
    });
    if (data.Success !== 'false' && Array.isArray(data.Committees)) {
      return data.Committees.map(c => c.CommitteeCode);
    }
    return [];
  } catch (error) {
    return [];
  }
};

function Events(props) {
  const {
    form,
    pagedata,
    DateRanges = { entities: {}, result: [] },
    MeetingTypes = { entities: {}, result: [] },
    States = { entities: {}, result: [] },
    Committees = { entities: {}, result: [] },
    isLoggedIn,
    messageData = {},
    personId,
  } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [eventsData, setEventsData] = useState({});
  const [searching, setSearching] = useState(true);
  const [designPageData, setPageData] = useState(pagedata);
  const [userCommittees, setUserCommittees] = useState([]);

  useEffect(() => {
    (async () => {
      if (personId) {
        const theCommittees = await getUserCommittees(personId);
        setUserCommittees(theCommittees);
      }
    })();
  }, [personId]);

  useEffect(() => {
    const fetchDesignPage = async () => {
      const pageResponse = await request({
        url: `cms/designpage`,
        params: {
          alias: 'heroslides',
        },
      });
      if (pageResponse.data?.DateLastUpdated !== pagedata?.DateLastUpdated) {
        setPageData(pageResponse.data);
      }
    };
    fetchDesignPage();
  }, []);

  async function formatValues(values) {
    const {
      dateRanges,
      meetingTypes = [],
      states = [],
      committees = [],
      searchText = '',
      customRange,
      myCommittees = false,
    } = values;

    const formattedValues = {
      SearchText: searchText,
      DateRanges: dateRanges ? [{ DayRangeCode: dateRanges }] : [],
      MeetingTypes: meetingTypes.map(mtId => ({ MeetingTypeId: mtId })),
      States: states.map(stId => ({ StateCode: stId })),
      Committees: myCommittees
        ? userCommittees.map(id => ({ CommitteeCode: id }))
        : committees.map(comId => ({ CommitteeCode: comId })),
    };

    if (customRange && Array.isArray(customRange) && customRange.length === 2) {
      const [dateStart, dateEnd] = customRange;
      if (typeof dateState !== 'string' && typeof dateEnd !== 'string') {
        // YYYY-MM-DD format comes from the query per https://www.benlcollins.com/spreadsheets/query-dates/
        formattedValues.DateStart = dateStart.format(DATE_FORMAT);
        formattedValues.DateEnd = dateEnd.format(DATE_FORMAT);
      } else {
        formattedValues.DateStart = moment(dateStart).format(DATE_FORMAT);
        formattedValues.DateEnd = moment(dateEnd).format(DATE_FORMAT);
      }
    }
    return formattedValues;
  }

  async function searchEvents(values) {
    const currentScrollPosition = window.scrollY;
    if (!isLoggedIn) return;
    setSearching(true);
    try {
      const formattedValues = await formatValues(values);
      if (values.show === undefined || values.show === 'upcoming') {
        formattedValues.DateStart = formattedValues.DateStart
          ? formattedValues.DateStart
          : moment(new Date()).format('MM/DD/YYYY');
      } else if (values.show === 'past') {
        formattedValues.DateEnd = formattedValues.DateEnd
          ? formattedValues.DateEnd
          : moment(new Date()).format('MM/DD/YYYY');
      }
      window.scrollTo({ top: currentScrollPosition, behavior: 'smooth' });
      const payload = JSON.stringify({
        FieldOptions: formattedValues,
      });

      const response = await request({
        method: 'POST',
        url: 'cms/eventssearch',
        data: payload,
        triggerLoad: false,
      });
      const { data } = response;

      if (!data.Count) {
        setEventsData({});
        setSearching(false);
      } else {
        setEventsData(data.Results);
        setTimeout(() => setSearching(false), 50);
      }
    } catch (error) {
      setEventsData({});
      setSearching(false);
    }
  }

  const page = new DesignPage({ ...designPageData, ...messageData });
  const blockData = page.getBlockDataByTypeId(10);

  return (
    <Layout>
      <Hero
        type="carousel"
        title={<span className="u-sr-only">Events</span>}
        css={style.heroContainer}
      >
        <HeroSlides blockData={blockData} entities={page.entities} />
      </Hero>
      <Section bgColor="gray">
        {isLoggedIn ? (
          <Container>
            <Row gutter={{ xs: 24, md: 32 }} type="flex">
              <Col xs={24} lg={collapsed ? 24 : 8}>
                <SearchFilters
                  form={form}
                  DateRanges={DateRanges}
                  MeetingTypes={MeetingTypes}
                  States={States}
                  Committees={Committees}
                  search={searchEvents}
                  collapsed={collapsed}
                  setCollapsed={setCollapsed}
                  userCommittees={userCommittees}
                />
              </Col>
              <Col xs={24} lg={collapsed ? 24 : 16}>
                <EventsList eventsData={eventsData} loading={searching} />
                {!eventsData.length > 0 && (
                  <p css={style.noRecordsFound}>No events match your search criteria</p>
                )}
              </Col>
            </Row>
          </Container>
        ) : (
          <Container css={style.contentbox} size="sm">
            <div css={style.nonmemberprompt}>
              <Heading type={2} weight="bolder" theme="primaryGray">
                Are you a member?
              </Heading>
              <p>
                <Link
                  anchor
                  href={`${process.env.AUTH_ROOT ||
                    ''}/pci/SignInTest?realm=${process.env.API_ROOT.replace(
                      'https',
                      'http'
                    )}/&redirect_uri=${encodeURIComponent(
                      !isServer ? window.location.href : ''
                    )}&deflate=false&sign_out=false`}
                >
                  Login
                </Link>{' '}
                to see the full calendar.
              </p>
            </div>
          </Container>
        )}
      </Section>
      <WebLogImage browserPath pagedata={designPageData} />
    </Layout>
  );
}

export async function getStaticProps() {
  const pageResponse = await request({
    url: `cms/designpage`,
    params: {
      alias: 'heroslides',
    },
  });

  const response = await searchContent({
    fieldOptionsToReturn: ['DateRanges', 'MeetingTypes', 'States', 'Committees'],
  });
  const dateRange = new schema.Entity('DateRanges', {}, { idAttribute: 'DayRangeCode' });
  const meetingType = new schema.Entity('MeetingTypes', {}, { idAttribute: 'MeetingTypeId' });
  const state = new schema.Entity('States', {}, { idAttribute: 'StateCode' });
  const committee = new schema.Entity('Committees', {}, { idAttribute: 'CommitteeCode' });

  const normalized = normalize(response.options, {
    DateRanges: [dateRange],
    MeetingTypes: [meetingType],
    States: [state],
    Committees: [committee],
  });

  return {
    props: {
      pagedata: pageResponse.data,
      DateRanges: {
        entities: normalized.entities.DateRanges,
        result: normalized.result.DateRanges,
      },
      MeetingTypes: {
        entities: normalized.entities.MeetingTypes,
        result: normalized.result.MeetingTypes,
      },
      States: {
        entities: normalized.entities.States,
        result: normalized.result.States,
      },
      Committees: {
        entities: normalized.entities.Committees,
        result: normalized.result.Committees,
      },
    },
  };
}

export default connect(mapStateToProps)(
  withMembershipAccessWrap(Form.create()(Events), null, true)
);
